import type { AssetGroup, OptimizerState } from '../types';

export const TERMINAL_STATES: OptimizerState[] = [
  'COMPLETED',
  'CANCELED',
  'FAILED',
];

export const REGISTERED_TYPES = [
  'TFSA',
  'RRSP',
  'RESP',
  'LIRA',
  'LIF',
  'RRIF',
  'RDSP',
  'FHSA',
  'DPSP',
];

export const TRIGGER_EXPLANATIONS: Record<string, string> = {
  Drift: 'Allocation drift exceeds threshold — rebalance to target weights',
  Transfer:
    'Cash deposit or withdrawal — adjust positions to maintain allocation',
  Liquidate: 'Full or partial liquidation required',
};

/* ───────────────────────────────────────────────── */
/*  Trade blotter (from tradeBlotter)                */
/* ───────────────────────────────────────────────── */

export const GROUP_ORDER: AssetGroup[] = [
  'CASH',
  'EQUITIES',
  'FIXED_INCOME',
  'OTHER',
];

export const GROUP_LABELS: Record<AssetGroup, string> = {
  CASH: 'Cash Equivalents',
  EQUITIES: 'Equities',
  FIXED_INCOME: 'Fixed Income',
  OTHER: 'Other',
};

export const POS_COLS = 5; // Security, Current$, Weight, Target, Drift
export const RES_COLS = 2; // Post$, Post%

/* ───────────────────────────────────────────────── */
/*  Bulk trade run workflow (from bulkTradeRunInlineWorkflow) */
/* ───────────────────────────────────────────────── */

export const STEPS = [
  { key: 'review' as const, label: 'Review', num: 1 },
  { key: 'update' as const, label: 'Update', num: 2 },
  { key: 'allocations' as const, label: 'Allocations', num: 3 },
  { key: 'complete' as const, label: 'Complete', num: 4 },
];

export const TRANSITION_LABELS: Record<string, string> = {
  generateBulkTradeFiles: 'Trade files generated',
  resetBulkTrades: 'Bulk trades reset',
  cancel: 'Trade run cancelled',
  initiateAllocations: 'Allocations initiated',
};

/* ───────────────────────────────────────────────── */
/*  Portfolios tab (from portfoliosTab)              */
/* ───────────────────────────────────────────────── */

export const PORTFOLIO_HEADER_COLS: Array<{
  id: string;
  label: string;
  width: string;
  minWidth?: string;
  right?: boolean;
  center?: boolean;
}> = [
  { id: 'priority', label: 'PRI', width: '50px' },
  { id: 'name', label: 'Portfolio', width: 'auto', minWidth: '180px' },
  { id: 'cash', label: 'Cash / Tgt', width: '120px', right: true },
  { id: 'marketValue', label: 'Mkt Value', width: '110px', right: true },
  { id: 'drift', label: 'Drift', width: '100px' },
  { id: 'trades', label: 'Trades', width: '60px', center: true },
];

export const VIEW_MODE_TOGGLES = [
  { key: 'portfolios' as const, getLabel: (n: number) => `Portfolios (${n})` },
  { key: 'exposure' as const, getLabel: () => 'Ticker' },
  { key: 'model' as const, getLabel: () => 'Model' },
] as const;

/* ───────────────────────────────────────────────── */
/*  Cross portfolio exposure (from crossPortfolioExposure) */
/* ───────────────────────────────────────────────── */

export const DIRECTION_CHIPS: {
  dir: 'SELL' | 'BUY' | 'REBALANCE';
  colorKey: 'negative' | 'positive' | 'primary';
}[] = [
  { dir: 'SELL', colorKey: 'negative' },
  { dir: 'BUY', colorKey: 'positive' },
  { dir: 'REBALANCE', colorKey: 'primary' },
];

/* ───────────────────────────────────────────────── */
/*  Hub-level UI strings (centralized for i18n)      */
/* ───────────────────────────────────────────────── */

export const CORE_TABS: Array<{
  accessor: 'today' | 'portfolios' | 'tradeRequests' | 'operations';
  name: string;
}> = [
  { accessor: 'today', name: 'Dashboard' },
  { accessor: 'portfolios', name: 'Portfolios' },
  { accessor: 'tradeRequests', name: 'Trades' },
  { accessor: 'operations', name: 'Active Operations' },
];
